import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle2, XCircle, Loader, Rocket } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const OnboardingSetup = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { updateUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [employee, setEmployee] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: '',
    });

    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        label: '',
        color: '',
    });

    useEffect(() => {
        verifyToken();
    }, [token]);

    const verifyToken = async () => {
        try {
            const response = await api.get(`/onboarding/verify/${token}`);
            const emp = response.data.employee;

            if (emp.password_set) {
                // Already set password, redirect to login
                navigate('/login');
            } else {
                setEmployee(emp);
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Invalid onboarding link');
        } finally {
            setLoading(false);
        }
    };

    const calculatePasswordStrength = (password) => {
        let score = 0;
        if (password.length >= 8) score++;
        if (password.length >= 12) score++;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[^a-zA-Z0-9]/.test(password)) score++;

        let label = '';
        let color = '';

        if (score === 0) {
            label = '';
            color = '';
        } else if (score <= 2) {
            label = 'Weak';
            color = 'bg-red-500';
        } else if (score <= 3) {
            label = 'Fair';
            color = 'bg-yellow-500';
        } else if (score <= 4) {
            label = 'Good';
            color = 'bg-primary';
        } else {
            label = 'Strong';
            color = 'bg-emerald-500';
        }

        return { score, label, color };
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        if (name === 'password') {
            setPasswordStrength(calculatePasswordStrength(value));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setSubmitting(true);

        try {
            const response = await api.post(`/onboarding/set-password/${token}`, {
                password: formData.password,
            });

            // Store token and user data
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));

            // Update auth context
            updateUser(response.data.user);

            // Redirect to employee dashboard
            navigate('/employee');
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to set password');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center p-4">
                <div className="text-center">
                    <Loader size={48} className="animate-spin text-accent mx-auto mb-4" />
                    <p className="text-gray-600">Verifying your invitation...</p>
                </div>
            </div>
        );
    }

    if (error && !employee) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <XCircle size={32} className="text-red-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="px-6 py-3 bg-accent text-white rounded-xl font-semibold hover:bg-accent-light transition-colors"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
                {/* Header */}
                <div className="bg-primary p-8 text-center">
                    <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Rocket size={32} className="text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-2">Welcome to {employee?.company_name}!</h1>
                    <p className="text-orange-50">Set your password to get started</p>
                </div>

                {/* Content */}
                <div className="p-8">
                    {/* Welcome Message */}
                    <div className="mb-6 p-4 bg-orange-50 rounded-xl">
                        <p className="text-sm text-gray-900">
                            <strong>Welcome, {employee?.first_name}!</strong>
                            <br />
                            Username: <span className="font-mono">{employee?.username}</span>
                        </p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                            <XCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-red-700 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Password */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Create Password <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition-all"
                                    placeholder="••••••••"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>

                            {/* Password Strength Bar */}
                            {formData.password && (
                                <div className="mt-2">
                                    <div className="flex gap-1 mb-1">
                                        {[1, 2, 3, 4, 5].map((level) => (
                                            <div
                                                key={level}
                                                className={`h-1 flex-1 rounded-full transition-all ${level <= passwordStrength.score ? passwordStrength.color : 'bg-gray-200'
                                                    }`}
                                            />
                                        ))}
                                    </div>
                                    {passwordStrength.label && (
                                        <p className="text-xs text-gray-600">
                                            Password strength: <span className="font-semibold">{passwordStrength.label}</span>
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Confirm Password <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition-all"
                                    placeholder="••••••••"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                    <XCircle size={14} />
                                    Passwords do not match
                                </p>
                            )}
                            {formData.confirmPassword && formData.password === formData.confirmPassword && (
                                <p className="text-xs text-emerald-500 mt-1 flex items-center gap-1">
                                    <CheckCircle2 size={14} />
                                    Passwords match
                                </p>
                            )}
                        </div>

                        {/* Info Box */}
                        <div className="p-4 bg-orange-50 rounded-xl">
                            <p className="text-xs text-gray-900">
                                After setting your password, you'll be logged in automatically and can start exploring your employee portal.
                            </p>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full py-4 bg-accent hover:bg-accent-light text-white rounded-xl font-semibold shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {submitting ? (
                                <>
                                    <Loader size={20} className="animate-spin" />
                                    Setting Password...
                                </>
                            ) : (
                                <>
                                    <Rocket size={20} />
                                    Get Started
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default OnboardingSetup;

